import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IListIconState, IUploadFormState } from "./types";
import { uploadContent, fetchContentList, fetchContentById, updateContent, deleteContent } from "./actions";

export const initialFormState: IUploadFormState = {
    title: '',
    description: '',
    contentType: '',
    featureCategory: '',
    newsFeed: false,
    listIcons: false,
    status: '',
    file: null,
};

const initialState: IListIconState = {
    contentUploading: 'idle',
    contentUploadMessage: null,
    contentUploadError: null,
    dataLoading: 'idle',
    data: {
      contentList: [],
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1,
    },
    error: null,
    contentDetailsLoading: 'idle',
    contentDetails: null,
    contentDetailsError: null,
    contentUpdateLoading: 'idle',
    updatedContent: null, 
    contentUpdateMessage: null,
    contentUpdateError: null,
    contentDeleteLoading: 'idle',
    contentDeleteMessage: null,
    contentDeleteError: null,
    uploadProgress: 0,
    uploadFormState: initialFormState,
    uploadAbortController: null,
  };

  export const listIconSlice = createSlice({
    name: "listIcon",
    initialState,
    reducers: {
      clearMessage(state) {
        state.contentUploadMessage = null;
        state.contentUpdateMessage = null;
        state.contentDeleteMessage = null;
        state.contentUpdateError = null;
        state.contentDeleteError = null;
      },
      clearError(state){
        state.error= null;
        state.contentUploadError= null;
      },
      setUploadProgress(state, action: PayloadAction<number>) {
        state.uploadProgress = action.payload;
      },
      setUploadFormState(state, action: PayloadAction<Partial<IUploadFormState>>) {
        state.uploadFormState = { ...state.uploadFormState, ...action.payload };
      },
      resetUploadFormState(state) {
          state.uploadFormState = initialFormState;
      },
      setUploadAbortController(state, action: PayloadAction<AbortController | null>) {
          state.uploadAbortController = action.payload;
      },
      cancelUpload(state) {
          state.uploadAbortController?.abort();
          state.uploadAbortController = null;
          state.contentUploading = 'idle';
          state.uploadProgress = 0;
      },
      resetUploadStatus(state) {
          state.contentUploading = 'idle';
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(uploadContent.pending, (state) => {
          state.contentUploading = 'pending';
          state.contentUploadError = null;
          state.uploadProgress = 0;
        })
        .addCase(uploadContent.fulfilled, (state, action) => {
          state.contentUploading = 'succeeded';
          state.contentUploadMessage= action.payload.message;
          state.uploadProgress = 0;
          state.uploadFormState = initialFormState;
          state.uploadAbortController = null;
        })
        .addCase(uploadContent.rejected, (state, action) => {
          state.uploadAbortController = null;
          state.uploadProgress = 0;

          if (action.meta.aborted) {
            state.contentUploading = 'idle';
          } else {
            state.contentUploading = 'failed';
            state.contentUploadError = action.payload?.message || action.error?.message || "An unexpected error occurred";
          }
        })
  
        .addCase(fetchContentList.pending, (state) => {
          state.dataLoading = 'pending';
          state.error = null;
        })
        .addCase(fetchContentList.fulfilled, (state, action) => {
          state.dataLoading = 'succeeded';
          state.data = action.payload.data;
        })
        .addCase(fetchContentList.rejected, (state, action: PayloadAction<any>) => {
          state.dataLoading = 'failed';
          state.error = action.payload.message;
        })
  
        .addCase(fetchContentById.pending, (state) => {
          state.contentDetailsLoading = 'pending';
          state.contentDetailsError = null;
        })
        .addCase(fetchContentById.fulfilled, (state, action) => {
          state.contentDetailsLoading = 'succeeded';
          state.contentDetails = action.payload.data;
        })
        .addCase(fetchContentById.rejected, (state, action: PayloadAction<any>) => {
          state.contentDetailsLoading = 'failed';
          state.contentDetailsError = action.payload.message;
        })
  
        .addCase(updateContent.pending, (state) => {
          state.contentUpdateLoading = 'pending';
          state.contentUpdateError = null;
        })
        .addCase(updateContent.fulfilled, (state, action) => {
          state.contentUpdateLoading = 'succeeded';
          state.updatedContent = action.payload.data;
          state.contentUpdateMessage = action.payload.message;
          
          const index = state.data?.contentList.findIndex(item => item.id === action.payload.data.id);
          if (index !== -1) {
              state.data.contentList[index] = { ...state.data.contentList[index], ...action.payload.data };
          }
          if (state.contentDetails && state.contentDetails.id === action.payload.data.id) {
              state.contentDetails = action.payload.data;
          }
        })
        .addCase(updateContent.rejected, (state, action: PayloadAction<any>) => {
          state.contentUpdateLoading = 'failed';
          state.contentUpdateError = action.payload.message;
        })
  
        .addCase(deleteContent.pending, (state) => {
          state.contentDeleteLoading = 'pending';
          state.contentDeleteError = null;
        })
        .addCase(deleteContent.fulfilled, (state, action) => {
          state.contentDeleteLoading = 'succeeded';
          state.contentDeleteMessage = action.payload.message;
        })
        .addCase(deleteContent.rejected, (state, action: PayloadAction<any>) => {
          state.contentDeleteLoading = 'failed';
          state.contentDeleteError = action.payload.message;
        });
    },
  });
  export const { clearMessage, clearError, setUploadProgress, setUploadFormState, resetUploadFormState, setUploadAbortController, cancelUpload, resetUploadStatus } = listIconSlice.actions;
  export default listIconSlice.reducer;
  